import { getProductImage } from "~/utils/productUtils";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { Card } from "~/features/configurator/Linear/components/Card";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import classNames from "classnames";
import CenterContents from "~/common/components/CenterContents";
import { styled } from "~/common/mui";
import { type RelatedProductData } from "~/features/productDetails/components/RelatedProducts/RelatedProductsBlock";
import Parameter from "~/common/components/Parameter";
import { trackPdpRelatedProductsClick } from "~/app/analytics/ga/ga";
import { IconSize } from "~/common/mui/icons/IconSize";
import { SchaefflerIdParameter } from "~/domains/product/components/ProductParams";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { type ProductData } from "~/domains/product/types/ProductData";
import { Translation } from "~/app/i18n/Translation";
import { useTranslation } from "react-i18next";
import { appendPurchaseParams } from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { Product } from "~/domains/product/Product";
import { ProductContext } from "~/domains/product/ProductContext";
import { Stack } from "~/common/mui/Stack/Stack";
import { C_SECONDARY_HOVER } from "~/common/components/icon/Colors";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

interface RelatedProductProps {
  mainProductDesignation: string;
  product: RelatedProductData;
}

export const MobileRelatedProductCard = ({ product, mainProductDesignation }: RelatedProductProps) => {
  const reco_place = usePurchasePlace();
  const relatedProductPdpAvailabilityEnabled = useKameleoonFlag(KAMELEOON_FLAGS.relatedProductPDPAvailability);
  const reco_type: PurchaseType = "rp";
  let onClick = undefined;
  let href = undefined;
  if (product.url) {
    onClick = () => trackPdpRelatedProductsClick(mainProductDesignation, product.designation);
    href = appendPurchaseParams(product.url, reco_place, reco_type);
  }

  const image = getProductImage(product, ImageFormat.thumbnail);

  return (
    <ProductContext value={product as ProductData}>
      <RelatedProductCardRoot
        className={"scha__related-product__card"}
        disableHover={!product.url}
        as={product.url ? "a" : "div"}
        href={href}
        onClick={onClick}
      >
        <AspectRatioImage src={image?.url} />
        <CardContent>
          <ProductDesignation
            className={"scha__related-product__card__designation"}
            isPreferred={product.preferred}
            designation={product.designation}
            size={"title-medium"}
            iconSize={IconSize.FONT_16}
          />
          <Stack gap={1}>
            <RelatedProductDescription className={"scha__related-product__card__description"}>
              <Translation {...product.shortDescription} />
            </RelatedProductDescription>
            {relatedProductPdpAvailabilityEnabled && (
              <Product.StockIndicator className="scha__related-product__card__stock" />
            )}
          </Stack>
        </CardContent>
      </RelatedProductCardRoot>
    </ProductContext>
  );
};

export const DesktopRelatedProductCard = ({ product, mainProductDesignation }: RelatedProductProps) => {
  const reco_place = usePurchasePlace();
  const relatedProductPdpAvailabilityEnabled = useKameleoonFlag(KAMELEOON_FLAGS.relatedProductPDPAvailability);
  const reco_type: PurchaseType = "rp";
  let onClick = undefined;
  let href = undefined;
  if (product.url) {
    onClick = () => trackPdpRelatedProductsClick(mainProductDesignation, product.designation);
    href = appendPurchaseParams(product.url, reco_place, reco_type);
  }

  const image = getProductImage(product, ImageFormat.thumbnail);
  const { t } = useTranslation();

  // TODO: Send packageAliases via PDP and fix this
  const customerMaterialNumber = (product.aliases || []).join(", ");
  return (
    <ProductContext value={product as ProductData}>
      <RelatedProductCardRoot
        className={"scha__related-product__card"}
        disableHover={!product.url}
        as={product.url ? "a" : "div"}
        href={href}
        onClick={onClick}
      >
        <RelatedProductCardImage className={"scha__related-product__card__image"}>
          {image && <img src={image.url} alt="" />}
        </RelatedProductCardImage>
        <div>
          <ProductDesignation
            className={"scha__related-product__card__designation"}
            isPreferred={product.preferred}
            designation={product.designation}
            size={"title-medium"}
            iconSize={IconSize.FONT_16}
          />
          <Stack gap={1}>
            <RelatedProductDescription className={"scha__related-product__card__description"}>
              <Translation {...product.shortDescription} />
            </RelatedProductDescription>
            {relatedProductPdpAvailabilityEnabled && (
              <Product.StockIndicator className="scha__related-product__card__stock" />
            )}
            <SchaefflerIdParameter productDescriptor={fakeDescriptorFromProduct(product as ProductData)} />
            {customerMaterialNumber && <Parameter label={t("addProduct.customerId")} value={customerMaterialNumber} />}
          </Stack>
        </div>
      </RelatedProductCardRoot>
    </ProductContext>
  );
};

interface AspectRatioImageProps {
  className?: string;
  src?: string;
  alt?: string;
}

/**
 * Image with a 16:9 ratio. In case src is not provided, it will still occupy space
 */
const AspectRatioImage = ({ className, src, alt }: AspectRatioImageProps) => {
  return (
    <div className={classNames("forced-ratio-container--9-16", className)}>
      <CenterImage>{src && <img src={src} alt={alt} />}</CenterImage>
    </div>
  );
};

const CenterImage = styled(CenterContents)({
  padding: "8px",
});

const CardContent = styled("div")({
  padding: "12px",
});

const RelatedProductCardRoot = styled(Card)(({ theme }) => ({
  height: "100%",
  color: theme.palette.text.primary,
  "&:hover .scha__product-designation": {
    color: C_SECONDARY_HOVER,
  },
  [theme.breakpoints.up("desktop-s")]: {
    display: "flex",
    position: "relative",
    padding: theme.spacing(2.5),
    textDecoration: "none",
  },
}));

const RelatedProductCardImage = styled("div")(({ theme }) => ({
  width: theme.spacing(15),
  height: theme.spacing(15),
  marginRight: theme.spacing(2.5),
  flex: "none",
}));

const RelatedProductDescription = styled("p")(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  ...theme.typography.titleSmall,
  color: theme.palette.text.primary,
}));
